import { watch, onBeforeMount } from "vue";
import axios from "@/lib/axios";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { useDepartmentSearchStore } from "@/stores/departmentSearch";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import type { Department } from "@/interfaces/department";
import { objectPick } from "@vueuse/core";

interface Props {
  params?: Params;
  immediate?: boolean;
}

interface Params {
  q?: string;
  includes?: string[];
  filters?: { [key: string]: string | number | boolean };
}

const getDepartments = async (params: Params): Promise<Department[]> => {
  const { data } = await axios.get<Department[]>(`/api/departments/search`, {
    params: {
      ...objectPick(params, ["q", "includes"]),
      ...params.filters,
      // Normalizar query: lowercase y trim para búsqueda case-insensitive
      ...(params.q && { q: params.q.toLowerCase().trim() }),
    },
  });

  return data;
};

const useAirportsSearch = (props?: Props) => {
  const { immediate = false, params = {} } = props || {};
  const queryClient = useQueryClient();
  const { setQuery, setDepartments, resetState } = useDepartmentSearchStore();
  const { query, departments } = storeToRefs(useDepartmentSearchStore());

  const reset = () => {
    queryClient.getQueryCache().find(["departmentsSearch"])?.reset();
    resetState();
  };

  onBeforeMount(() => {
    reset();
    if (immediate) {
      refetch();
    }
  });

  const { data, isLoading, refetch, isError, error } = useQuery(
    ["departmentsSearch"],
    () => getDepartments({ q: query.value || undefined, ...params }),
    {
      enabled: false,
      retry: false,
    }
  );

  watch(query, () => {
    searchAirports();
  });

  watch(data, () => {
    if (data.value) {
      setDepartments(data.value);
    }
  });

  const searchAirports = useDebounceFn(() => {
    if (query.value.length) {
      refetch();
    }
  }, 500);
  return {
    query,
    departments,
    data,
    isLoading,
    isError,
    error,

    refetch,
    setQuery,
    setDepartments,
  };
};

export default useAirportsSearch;
